import { useState } from 'react';
import { useNavigate } from 'react-router';
import { MeasurementsGraph } from '../../components/measurement-graph/measurementsGraph.tsx';
import { MeasurementGraphControl } from '../../components/measurement-graph/settings/measurementGraphControl.tsx';
import { useSettings } from '../../components/measurement-graph/settings/useSettings.ts';
import { PageTitle } from '../../components/pageTitle.tsx';
import { ProtocolPreviewDialog } from '../../components/protocol-preview-dialog/protocolPreviewDialog.tsx';
import { SigmaHistogram } from '../../components/sigma-histogram/sigmaHistogram.tsx';
import { Overview } from './overview.tsx';
import { PositionDetails } from './positionDetails.tsx';
import { RangeSelect } from './rangeSelect.tsx';
import { SpcInfoDialog } from './spcInfoDialog.tsx';
import { SpcTogglesTable } from './spcTogglesTable.tsx';
import { useControlValueOverrides } from './useControlValueOverrides.ts';
import { useFilterData } from './useFilterData.ts';
import { useLoadRecords } from './useLoadRecords.ts';
import { useMGraphAdapter } from './useMGraphAdapter.ts';
import { useRangeOptions } from './useRangeOptions.ts';
import { useSpcResults } from './useSpcCheckFull.ts';
import { useSpcToggles } from './useSpcToggles.ts';

export function SpcDetails() {
    const searchParams = new URLSearchParams(window.location.search);
    const equipment = searchParams.get('equipment') as string;
    const product = searchParams.get('product') as string | null;
    const partName = searchParams.get('partName') as string | null;

    const [activePositionIdx, setActivePositionIdx] = useState<number | null>(null);
    const [showPdfDialog, setShowPdfDialog] = useState(false);
    const [showInfoDialog, setShowInfoDialog] = useState(false);

    const navigate = useNavigate();
    const { settings, setSettings } = useSettings();
    const { overrides, setOverrides } = useControlValueOverrides();

    const { allRecords, recordsPerBatch, protocol, batches, allOperations, operationsPerBatch } = useLoadRecords({ equipment, product, partName });

    const { rangeOptions, rangeSelection, setRangeSelection } = useRangeOptions({ allRecords, recordsPerBatch, batches });

    const { filteredRecords, filteredOperations } = useFilterData({
        allRecords,
        recordsPerBatch,
        batches,
        rangeSelection,
        allOperations,
        operationsPerBatch,
    });

    const { spcTogglesMap, handleSpcTogglesChange } = useSpcToggles({ protocol });
    const { spcResults, issuesCount } = useSpcResults({ protocol, filteredRecords, spcTogglesMap, overrides });
    const { measurements } = useMGraphAdapter({ spcResults, activePositionIdx, spcTogglesMap });

    return (
        <>
            <PageTitle title='Spc details' />
            <div className='measurement-container'>
                <div className='measurement-main-pane spc-details-main-pane'>
                    <div className='spc-top-toolbar'>
                        <RangeSelect
                            style={{ height: 'auto' }}
                            rangeOptions={rangeOptions}
                            rangeSelection={rangeSelection}
                            setRangeSelection={setRangeSelection}
                            onInfoClick={() => setShowInfoDialog(true)}
                            onClose={() => navigate('/spc-list')}
                        />
                    </div>

                    {protocol && filteredRecords && spcResults && activePositionIdx != null && (
                        <>
                            <div className='spc-graph-controls-row'>
                                <MeasurementGraphControl style={{ margin: '0 auto' }} settings={settings} setSettings={setSettings} />
                                <button onClick={() => setShowPdfDialog(true)}>View pdf</button>
                            </div>

                            <MeasurementsGraph
                                style={{ width: 'calc(100% - 24px)', height: '40%', margin: '12px 12px 0' }}
                                measurements={measurements}
                                controlValues={spcResults[activePositionIdx].controlValues}
                                batches={batches}
                                operations={filteredOperations}
                                openDialogOnClick={false}
                                settings={{ ...settings, showAxisLabels: true }}
                            />

                            <div className='spc-details-bottom-pane'>
                                <SigmaHistogram
                                    style={{ width: '600px', height: '300px', marginTop: '12px' }}
                                    spcResult={spcResults[activePositionIdx]}
                                />

                                <PositionDetails
                                    style={{ width: '300px', marginTop: '24px' }}
                                    spcResult={spcResults[activePositionIdx]}
                                    overrides={overrides}
                                    setOverrides={setOverrides}
                                />
                            </div>
                        </>
                    )}

                    {activePositionIdx == null && <div style={{ marginLeft: '40px', marginTop: '20px' }}>Select a position to view details</div>}
                </div>

                <div className='measurement-side-pane measurement-side-pane--spc' style={{ overflowY: 'auto' }}>
                    <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                        {protocol && filteredRecords && (
                            <Overview
                                equipment={equipment}
                                protocol={protocol}
                                enabledIssues={issuesCount?.enabledIssues}
                                allIssues={issuesCount?.allIssues}
                            />
                        )}

                        {protocol && spcTogglesMap && spcResults && (
                            <SpcTogglesTable
                                style={{ marginTop: '12px' }}
                                protocol={protocol}
                                spcResults={spcResults}
                                activePositionIdx={activePositionIdx}
                                setActivePositionIdx={setActivePositionIdx}
                                spcTogglesMap={spcTogglesMap}
                                onSpcTogglesChange={handleSpcTogglesChange}
                            />
                        )}
                    </div>
                </div>

                <ProtocolPreviewDialog
                    protocol={protocol}
                    initialPositionIdx={activePositionIdx}
                    show={showPdfDialog}
                    onClose={() => setShowPdfDialog(false)}
                />

                <SpcInfoDialog show={showInfoDialog} onClose={() => setShowInfoDialog(false)} />
            </div>
        </>
    );
}
